import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAssignTrailer } from '../../hooks/trailers/use-assign-trailer'
import { useUnassignTrailer } from '../../hooks/trailers/use-unassign-trailer'
import { useTrailers } from '../../hooks/use-get-trailers'

import type { TrailerWithVehicleResponse } from '@repo/api'

import FormControl from '@/components/shared/form-control'

type Props = {
  truckId: number
  currentTrailerId?: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function AssignTrailerDialog({
  truckId,
  currentTrailerId,
  open,
  onOpenChange,
}: Props) {
  const { t } = useTranslation()
  const { data: trailers } = useTrailers({
    unassignedOnly: true,
    includeTrailerId: currentTrailerId,
  })
  const { mutate: assignTrailer, isPending } = useAssignTrailer()
  const { mutate: unassignTrailer, isPending: isUnassigning } =
    useUnassignTrailer()

  const [selectedTrailerId, setSelectedTrailerId] = useState<string | null>(
    currentTrailerId?.toString() || null,
  )

  useEffect(() => {
    if (open) {
      setSelectedTrailerId(currentTrailerId?.toString() || '')
    }
  }, [open, currentTrailerId])

  const handleAssign = () => {
    if (!selectedTrailerId) return
    assignTrailer(
      { trailerId: Number(selectedTrailerId), truckId },
      { onSuccess: () => onOpenChange(false) },
    )
  }

  const handleUnassign = () => {
    if (!currentTrailerId) return
    unassignTrailer(
      { trailerId: currentTrailerId, truckId },
      { onSuccess: () => onOpenChange(false) },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('vehicles:assignTrailerDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('vehicles:assignTrailerDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <FormControl
            htmlFor="trailer"
            labelText={t('vehicles:assignTrailerDialog.selectTrailer')}
          >
            <Select
              value={selectedTrailerId || undefined}
              onValueChange={(value) => {
                if (value === '') return
                setSelectedTrailerId(value)
              }}
            >
              <SelectTrigger id="trailer" className="bg-background w-full">
                <SelectValue
                  placeholder={t(
                    'vehicles:assignTrailerDialog.selectTrailerPlaceholder',
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {trailers?.map((trailer: TrailerWithVehicleResponse) => (
                  <SelectItem key={trailer.id} value={String(trailer.id)}>
                    {trailer.licensePlate} ({trailer.trailer.length}m x{' '}
                    {trailer.trailer.width}m x {trailer.trailer.height}m)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
        </div>

        <DialogFooter className="flex items-center justify-between">
          <Button
            variant="outline"
            isLoading={isUnassigning}
            onClick={handleUnassign}
            disabled={!currentTrailerId || isUnassigning}
            className="min-w-32"
          >
            {t('vehicles:assignTrailerDialog.removeTrailer')}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common:cancel')}
            </Button>
            <Button
              isLoading={isPending}
              onClick={handleAssign}
              disabled={!selectedTrailerId || isPending}
              className="w-28"
            >
              {t('vehicles:assignTrailerDialog.assign')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
